import { createContext, useContext } from 'react';

import {
  FareEstimateFormAssets,
  FareEstimateFormErrorMsg,
  FareEstimateFormLabel,
  FareEstimateFormPlaceholder,
  FareEstimateRequirementContent,
} from '../configs/Store.store';
import {
  estimate_sub_title,
  estimate_title,
  requirement_business,
  requirement_personal,
  requirement_title,
  whatsappConsentText,
} from '../store/FareEstimateStore.store';

export interface FareEstimateContentIcon {
  url: string;
  alt: string;
}

export interface FareEstimateContentTextSubtext {
  text: string;
  sub_text: string;
}

export interface FareEstimateContentField {
  label: string;
  placeholder: string;
  error_msg: string;
  required: boolean;
  icon: FareEstimateContentIcon;
}

export interface FareEstimateContentRequirementOption {
  title: string;
  value_label: string;
  desc: string;
  icon: FareEstimateContentIcon;
}

export interface FareEstimateContentFieldParams {
  pickup: FareEstimateContentField;
  drop: FareEstimateContentField;
  name: FareEstimateContentField;
  phone: FareEstimateContentField;
  requirement: FareEstimateContentField;
}

export interface FareEstimateFormContent {
  personal_use_label: string;
  business_use_label: string;
  field_params: FareEstimateContentFieldParams;
  modal_title: string;
  modal_close_icon: FareEstimateContentIcon;
  get_estimate_label: string;
  get_estimate_icon: FareEstimateContentIcon;
  consent_label: string;
  vehcile_modal_title: string;
  city_title_desc: FareEstimateContentTextSubtext;
  location_icon: FareEstimateContentIcon;
  drop_down_icon: FareEstimateContentIcon;
}

export interface SpotFareEstimateFormContent {
  field_params: FareEstimateContentFieldParams;
  business_option: FareEstimateContentRequirementOption;
  personal_option: FareEstimateContentRequirementOption;
  requirement_title: string;
  get_estimate_label: string;
  get_estimate_icon: FareEstimateContentIcon;
  consent_label: string;
  city_title_desc: FareEstimateContentTextSubtext;
  modal_title: string;
  modal_close_icon: FareEstimateContentIcon;
}

export interface FareEstimateContent {
  fare_estimate_content: FareEstimateFormContent;
  spot_fare_estimate_content: SpotFareEstimateFormContent;
  right_arrow_icon: FareEstimateContentIcon;
}

const toIcon = (asset: {
  icon: string;
  alt: string;
}): FareEstimateContentIcon => ({ url: asset.icon, alt: asset.alt });

const defaultFieldParams: FareEstimateContentFieldParams = {
  pickup: {
    label: FareEstimateFormLabel.pickup_label,
    placeholder: FareEstimateFormPlaceholder.pickup,
    error_msg: FareEstimateFormErrorMsg.pickup,
    required: true,
    icon: toIcon(FareEstimateFormAssets.pickup),
  },
  drop: {
    label: FareEstimateFormLabel.drop_label,
    placeholder: FareEstimateFormPlaceholder.drop,
    error_msg: FareEstimateFormErrorMsg.drop,
    required: true,
    icon: toIcon(FareEstimateFormAssets.drop),
  },
  name: {
    label: FareEstimateFormLabel.name_label,
    placeholder: FareEstimateFormPlaceholder.name,
    error_msg: FareEstimateFormErrorMsg.name_required,
    required: true,
    icon: toIcon(FareEstimateFormAssets.user),
  },
  phone: {
    label: FareEstimateFormLabel.phone_label,
    placeholder: FareEstimateFormPlaceholder.mobile,
    error_msg: FareEstimateFormErrorMsg.mobile_required,
    required: true,
    icon: toIcon(FareEstimateFormAssets.call),
  },
  requirement: {
    label: FareEstimateFormLabel.requirement_title_desktop_label,
    placeholder: FareEstimateFormLabel.requirement_desc_label,
    error_msg: FareEstimateFormErrorMsg.requirement,
    required: true,
    icon: toIcon(FareEstimateFormAssets.dropdown),
  },
};

const defaultBusinessOption: FareEstimateContentRequirementOption = {
  title: FareEstimateRequirementContent.business.title,
  value_label: requirement_business,
  desc: FareEstimateRequirementContent.business.desc,
  icon: toIcon(FareEstimateFormAssets.business),
};

const defaultPersonalOption: FareEstimateContentRequirementOption = {
  title: FareEstimateRequirementContent.personal.title,
  value_label: requirement_personal,
  desc: FareEstimateRequirementContent.personal.desc,
  icon: toIcon(FareEstimateFormAssets.personal),
};

export const defaultFareEstimateContent: FareEstimateContent = {
  fare_estimate_content: {
    personal_use_label: FareEstimateFormLabel.personal,
    business_use_label: FareEstimateFormLabel.business,
    field_params: defaultFieldParams,
    modal_title: estimate_title,
    modal_close_icon: toIcon(FareEstimateFormAssets.dropdown),
    get_estimate_label: FareEstimateFormLabel.estimate_button,
    get_estimate_icon: toIcon(FareEstimateFormAssets.rightArrow),
    consent_label: whatsappConsentText,
    vehcile_modal_title: estimate_title,
    city_title_desc: { text: estimate_title, sub_text: estimate_sub_title },
    location_icon: toIcon(FareEstimateFormAssets.pickup),
    drop_down_icon: toIcon(FareEstimateFormAssets.dropdown),
  },
  spot_fare_estimate_content: {
    field_params: defaultFieldParams,
    business_option: defaultBusinessOption,
    personal_option: defaultPersonalOption,
    requirement_title,
    get_estimate_label: FareEstimateFormLabel.estimate_button,
    get_estimate_icon: toIcon(FareEstimateFormAssets.rightArrow),
    consent_label: whatsappConsentText,
    city_title_desc: { text: estimate_title, sub_text: estimate_sub_title },
    modal_title: estimate_title,
    modal_close_icon: toIcon(FareEstimateFormAssets.dropdown),
  },
  right_arrow_icon: toIcon(FareEstimateFormAssets.rightArrow),
};

export const FareEstimateContentContext = createContext<FareEstimateContent>(
  defaultFareEstimateContent,
);

export const useFareEstimateContent = (): FareEstimateContent =>
  useContext(FareEstimateContentContext);
